import { useMutation, useQuery, keepPreviousData } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { fetchClientes, createClient, updateClient, deleteClient, fetchAllClients, fetchClientsWithPlazas, fetchClientDetails, fetchClientEvents, fetchClientByTelefono, fetchClientPortalStatus, fetchOpcionesAlta, fetchOpcionesTarifa, resendClientInvite,fetchClientActivity, fetchClientRecoveries, fetchClientAttendance ,grantClientRecovery, revokeClientRecovery } from '../api';
import { ClientActivityPage } from '../api/clienteService';
import { Client } from "../../types/index";
import { EventoWithDetails } from '../../types/eventos';
import { useTenantCode } from './useTenantCode';

interface UpdateClientParams {
    id: string;
    data: Partial<Client>;
}

// Query hook para obtener clientes
export const useClientsQuery = (options?: { enabled?: boolean }) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['clients', tenantCode],
        queryFn: fetchClientes,
        ...options
    });
};

export const useAllClientsQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['clients', 'all', tenantCode],
        queryFn: fetchAllClients
    });
};

// Nuevo hook para buscar cliente por teléfono
export const useClientByTelefonoQuery = (telefono?: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['client', 'telefono', telefono, tenantCode],
        queryFn: () => fetchClientByTelefono(telefono!),
        enabled: !!telefono && telefono.trim() !== '',
        retry: false,
        staleTime: 1000 * 60 * 2,
        refetchOnWindowFocus: false,
    });
};

// Nuevo hook para listado con plazas
export const useClientsWithPlazasQuery = (options = { includeInactive: false }) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['clients', 'with-plazas', options.includeInactive, tenantCode],
        queryFn: () => fetchClientsWithPlazas(options.includeInactive)
    });
};


// Hook para detalle completo de cliente
export const useClientDetailsQuery = (clientId?: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['client', clientId, tenantCode],
        queryFn: () => fetchClientDetails(clientId!),
        enabled: !!clientId
    });
};


// Hook para obtener los eventos de un cliente
export const useClientEventsQuery = (
    clientId?: string, 
    options: { 
        limit?: number;
        enabled?: boolean;
    } = {}
) => {
    const { limit = 5, enabled = true } = options;
    const tenantCode = useTenantCode();
    
    return useQuery<EventoWithDetails[]>({
        queryKey: ['client', clientId, 'events', limit, tenantCode],
        queryFn: () => fetchClientEvents(clientId!, { limit }),
        enabled: !!clientId && enabled,
        staleTime: 5 * 60 * 1000,
    });
};


// Historial de actividad del cliente (G1), paginado
export const useClientActivityQuery = (
    clientId?: string,
    options: { page?: number; limit?: number; enabled?: boolean } = {}
) => {
    const { page = 1, limit = 20, enabled = true } = options;
    const tenantCode = useTenantCode();
    return useQuery<ClientActivityPage>({
        queryKey: ['client', clientId, 'activity', page, limit, tenantCode],
        queryFn: () => fetchClientActivity(clientId!, page, limit),
        enabled: !!clientId && enabled,
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
    });
};

// Recuperaciones pendientes del cliente (M10) — visibilidad para el admin
export const useClientRecoveriesQuery = (clientId?: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['client', clientId, 'recoveries', tenantCode],
        queryFn: () => fetchClientRecoveries(clientId!),
        enabled: !!clientId,
        staleTime: 60 * 1000,
    });
};

// Otorgar una recuperación a mano (solo admin). Invalida también la actividad
// del cliente, porque el otorgamiento queda registrado en su historial.
export const useGrantClientRecoveryMutation = (clientId?: string) => useMutation({
    mutationFn: (payload: { levelId: string; note?: string }) =>
        grantClientRecovery(clientId!, payload),
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['client', clientId, 'recoveries'] });
        queryClient.invalidateQueries({ queryKey: ['client', clientId, 'activity'] });
        toast.success('Recuperación otorgada');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
        toast.error(error?.response?.data?.message ?? 'No se pudo otorgar la recuperación');
    },
});

export const useRevokeClientRecoveryMutation = (clientId?: string) => useMutation({
    mutationFn: ({ recoveryId, reason }: { recoveryId: string; reason?: string }) =>
        revokeClientRecovery(clientId!, recoveryId, reason),
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['client', clientId, 'recoveries'] });
        // La retirada deja una fila en el historial, así que hay que refrescarlo.
        queryClient.invalidateQueries({ queryKey: ['client', clientId, 'activity'] });
        toast.success('Recuperación retirada');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
        toast.error(error?.response?.data?.message ?? 'No se pudo revocar la recuperación');
    },
});

// Mutación para crear clientes
export const useCreateClientMutation = () => useMutation({
    mutationFn: createClient,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        toast.success('Cliente creado exitosamente');
    },
    onError: () => {
        toast.error('Error al crear el cliente');
    },
});

// Mutación para actualizar clientes
export const useUpdateClientMutation = () => useMutation({
    mutationFn: ({id, data}: UpdateClientParams) => updateClient(id, data),
    onSuccess: (data, variables) => {
        // Invalidar todas las consultas relacionadas con clientes
        queryClient.invalidateQueries({
            queryKey: ['clients']
        });

        // Invalidar específicamente los detalles del cliente que se actualizó
        queryClient.invalidateQueries({
            queryKey: ['client', variables.id]
        });

        // B5 — el estado de la cuenta de portal depende del correo, así que si se
        // ha cambiado hay que refrescarlo.
        if (data?.portalSync?.changed) {
            // Prefijo de ['client', id, 'portal-status', tenantCode], que ya lo
            // cubre la invalidación de ['client', id] de arriba — se deja
            // explícito porque ese estado depende del correo y tiene staleTime.
            queryClient.invalidateQueries({ queryKey: ['client', variables.id, 'portal-status'] });
        }

        toast.success('Cliente actualizado exitosamente');

        // B5 — el correo se ha guardado igualmente, pero si no se pudo arrastrar
        // a Clerk el cliente se queda accediendo con el anterior. Sin este aviso
        // el desfase es invisible, que es justo como se produjo el bug.
        if (data?.portalSync?.changed && !data.portalSync.synced) {
            toast.warning(
                '⚠️ El correo se ha cambiado, pero no se pudo actualizar su acceso al portal. Seguirá entrando con el anterior: revisa su cuenta o reenvíale la invitación.',
                {
                    autoClose: false,
                    position: 'top-center',
                    toastId: `portal-sync-${variables.id}`,
                    className: 'border-l-4 border-yellow-500',
                }
            );
        }

        // Cambiar el correo de quien aún no había entrado al portal revoca su
        // invitación anterior y manda una nueva: conviene decirlo, porque le
        // llega un correo que el administrador no ha pedido explícitamente.
        if (data?.portalSync?.synced && data.portalSync.mode === 'reinvited') {
            toast.info('Se ha enviado una nueva invitación al portal a la dirección nueva.');
        }

        // Se quitó el correo a quien tenía una invitación sin aceptar: se anula.
        if (data?.portalSync?.synced && data.portalSync.mode === 'invitation-revoked') {
            toast.info('Se ha anulado la invitación al portal que tenía pendiente.');
        }
    },
    onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Error al actualizar el cliente');
    },
});

// Estado de la cuenta de portal de un cliente
export const useClientPortalStatusQuery = (id?: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['client', id, 'portal-status', tenantCode],
        queryFn: () => fetchClientPortalStatus(id!),
        enabled: !!id,
        staleTime: 1000 * 60, // 1 min
    });
};

// Cómo aparece marcada la casilla "Invitar al portal" en el alta (ajuste del
// centro). Aparte de /configurations porque esa exige admin.
export const useOpcionesAltaQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['clients', 'opciones-alta', tenantCode],
        queryFn: fetchOpcionesAlta,
        staleTime: 1000 * 60 * 5,
    });
};

// Tarifas del centro para el filtro de la lista. Aparte de /configurations
// porque esa exige admin.
export const useOpcionesTarifaQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['clients', 'opciones-tarifa', tenantCode],
        queryFn: fetchOpcionesTarifa,
        staleTime: 1000 * 60 * 5,
    });
};

// Mutación para reenviar la invitación de portal
export const useResendClientInviteMutation = () => useMutation({
    mutationFn: resendClientInvite,
    onSuccess: (_data, id) => {
        queryClient.invalidateQueries({ queryKey: ['client', id, 'portal-status'] });
        // La lista enseña la fecha de invitación y filtra por ella.
        queryClient.invalidateQueries({ queryKey: ['clients', 'with-plazas'] });
        toast.success('Invitación enviada');
    },
    onError: (error: unknown) => {
        const message =
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Error al reenviar la invitación';
        toast.error(message);
    },
});

// Mutación para eliminar clientes
export const useDeleteClientMutation = () => useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        toast.success('Cliente eliminado exitosamente');
    },
    onError: () => {
        toast.error('Error al eliminar el cliente');
    },
});
// P6 — asistencia del cliente, desde su propia colección (sin TTL).
export const useClientAttendanceQuery = (clientId?: string, page = 1) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['client', clientId, 'attendance', page, tenantCode],
        queryFn: () => fetchClientAttendance(clientId!, page),
        enabled: !!clientId,
        staleTime: 60 * 1000,
    });
};
